/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppVoiceRecorder, AppButton, getSecondaryButtonOverrideStyles } from '../../components';
import { Label, MessageBarType, Link, Dialog,  DialogFooter, Image } from 'office-ui-fabric-react';
import { getRecordingContainerStyles, getWarningMessageBarStyles, getPrivacyDisclaimerStyles, getPrivacyPolicyLinkStyle, getPrivacyDisclaimerTextStyles, getNeedHelpLinkStyle, getMicrophoneImageContainerStyle, getBackupDisabledStyles, getBackupDisabledTextStyles } from '../../common/pageStyles/RecordingPage.styles';
import { SetForegroundTaskInStore } from '../../common/actions/LoadingActions';
import { RouteComponentProps } from 'react-router-dom';
import { TranscriptionRoutePathNames } from '../../routes';
import { LiveRecordingSession } from '../../../backend/common/liveRecording/LiveRecordingSession';
import recordingStore from '../../stores/RecordingStore';
import { ChangeEvent } from '../../stores/utils';
import { SetRecorderIsPausedInStore, SetRecorderReadyInStore, SetMicrophoneStateInStore, SetWarningDismissedInStore, SetPrivacyDisclaimerClosedFlag, SetMicrophonePermissionRetryDialog, SetRecordingDone } from '../../common/actions/RecordingActions';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { LogError } from '../../../utils/ErrorTelemetry';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { TaskList, TaskListResultStatus } from '../../../backend/transcriptionManager/TaskList';
import { SetFailureStateAndRouteToFailure } from '../../actions/FailureActions';
import { CheckNetworkConnection, GetNetworkConnectionMonitorInstance, InternetState, NetworkConnectionListener } from '../../NetworkConnectionMonitor';
import { NetworkError, NetworkErrors } from '../../../utils/Errors';
import { isUserMicrophoneError } from '../../../backend/transcriptionManager/RealTimeTranscriptionConnectionErrors';
import { LiveRecordingKeys } from '../../../backend/telemetry/LiveRecordingKeys';
import { LiveRecordingForegroundTask } from '../../../backend/liveRecording/LiveRecordingForegroundTask';
import { AppAuthenticationProviderContext } from '../../context';
import { ErrorStringFromException, isNullOrUndefined } from '../../../utils/utils';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { MicrophoneKeys } from '../../common/telemetry/MicrophoneKeys';
import { AugloopError, AugloopErrors } from '../../../backend/augloop/AugloopErrors';
import { PRIVACY_POLICY_URL, MICROPHONE_PERMISSION_HELP_PAGE_URL, PERMISSION_GRANTED, HOST_PLATFORM_WIN32 } from '../../../AppConstants';
import { AppMessageBar } from '../../components/AppMessageBar/AppMessageBar';
import { TranscriptionTelemetryErrorCodes } from '../../../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../../../backend/interfaces/ITranscriptionErrorEvent';
import { getDialogContentProps, getDialogStyles, getModalProps, getTopLabelStyles } from '../../common/styles/Popup.styles';
import { MicrophoneState } from '../../common/enums/recordingEnums';
import { FailureContinuation } from '../../common/enums/failureEnums';
import { getHostAppInfoBar } from '../../components/HostAppInfoBar/HostAppInfoBar';
import { TranscriptionAlert } from '../../components/Popups/TranscriptionAlert';
import { HostAppUtil } from '../../../backend/office-js/HostAppUtil';
import { HostAppSettingName, HostAppSettings } from '../../../backend/office-js/HostAppSettings';
import { NavigateToAssociatedPage } from '../../../backend/OneNote/OneNoteTranscriptionManager';
import { LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE } from '../../LoadTranscriptionStrings';

export interface RecordingState {
  isRecorderReady: boolean;
  isPaused: boolean;
  microphoneState: MicrophoneState;
  warningDismissed: boolean;
  privacyDisclaimerClosed: boolean;
  shouldShowMicrophonePermissionRetryDialog: boolean;
}

export class Recording extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>, RecordingState>
implements NetworkConnectionListener {
  private session: LiveRecordingSession | null = null;
  private initRecordingTaskList: TaskList<any> | null = null;
  private _isMounted: boolean;
  private isAudioBackAvailable: boolean;
  private appVoiceRecorderContainerRef = React.createRef<HTMLDivElement>();
  private warningContainerRef = React.createRef<HTMLDivElement>();
  static contextType = AppAuthenticationProviderContext;

  constructor(props: RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>) {
    super(props);
    this._isMounted = false;
    this.isAudioBackAvailable = false;

    this.state = recordingStore.getState();
    recordingStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async UNSAFE_componentWillMount() {
    try {
      LogEvents.LiveRecording.Start();
      LogEvents.LiveRecording.BeginDurationMeasure(DurationMeasureType.Normal, LiveRecordingKeys.InitializationTotalDuration);
      CheckNetworkConnection();
      if (this.props.location.state.realTimeTranscriptionConnection == null) {
        throw new Error('realTimeTranscriptionConnection is null inside Recording component');
      }

      if (this.props.location.state.audioBackup == null) {
        LogError(LogEventNames.Warning, 'audioBackup is null inside Recording component');
        this.isAudioBackAvailable = false;
      } else {
        this.isAudioBackAvailable = this.props.location.state.audioBackup.IsInitializedSuccessfully();
      }

      LogEvents.LiveRecording.AddData([{ key: LiveRecordingKeys.IsAudioBackupEnabled, value: this.isAudioBackAvailable }]);
      this.session = new LiveRecordingSession(
        this.props.location.state.realTimeTranscriptionConnection,
        (error: Error) => this.onErrorCallback(error),
        this.props.location.state.audioBackup);

      this.props.location.state.audioBackup = null;
      this.props.location.state.realTimeTranscriptionConnection = null;

      this.initRecordingTaskList = new TaskList([
        () => this.session!.Initialize(),
        () => this.InitializeMicrophoneState()
      ]);

      const result = await this.initRecordingTaskList.ExecuteTaskListSequentially();

      if (result.status == TaskListResultStatus.Canceled) {
        if (!isNullOrUndefined(this.session)) {
          await this.session.Pause();
        }
        return;
      }

      SetRecorderReadyInStore(true);
      LogEvents.LiveRecording.EndDurationMeasure(LiveRecordingKeys.InitializationTotalDuration);
    } catch (e) {
      let failureMessage = TranscriptionStrings.UnknownRecordingFailureReason;
      let failureLoggingMessage = TranscriptionTelemetryErrorCodes.UnknownRecordingFailureReason;
      if (!this.isAudioBackAvailable) {
        failureMessage = TranscriptionStrings.UnknownInitFailureReason;
        failureLoggingMessage = TranscriptionTelemetryErrorCodes.UnknownInitFailureReason;
      }

      let failureContinuation = FailureContinuation.StartOver;

      if ((e instanceof NetworkError && e.name == NetworkErrors.NoInternetConnection)
          || (e instanceof AugloopError && e.name == AugloopErrors.SessionInitTimeout)) {
        failureMessage = TranscriptionStrings.ConnectionIssueFailureReason;
        failureLoggingMessage = TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason;
        failureContinuation = FailureContinuation.Retry;
      }

      const failureReasonDebug: string = 'Recording componentWillMount threw error : ' + ErrorStringFromException(e);
      const transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage: failureMessage, errorEventName: failureLoggingMessage}
      await this.handleFailure(transcriptionErrorEvent, failureContinuation, failureReasonDebug, this.props.history);
    }
  }

  public async componentDidMount() {
    this._isMounted = true;

    // Todo: Move this flow to OneNoteTranscriptionManager when call
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.LinkedPageNavigationDuringRecordingEnabled)) {
      NavigateToAssociatedPage().catch((e) => {
        LogError(LogEventNames.Warning,
          "Navigation to linked page failed during start of live recording with error " +
          ErrorStringFromException(e));
      });
    }
    GetNetworkConnectionMonitorInstance().addListener(this);
  }

  public async componentWillUnmount() {
    this.initRecordingTaskList!.Cancel();

    if (this.state.isRecorderReady)
      await this.session!.Pause();

    this._isMounted = false;
    GetNetworkConnectionMonitorInstance().removeListener(this);
  }

  private isWin32() {
    return HostAppUtil.GetHostAppPlatform() == HOST_PLATFORM_WIN32;
  }

  private getMicrophonePermissionErrorTitle() {
    return this.isWin32()
      ? TranscriptionStrings.MicrophonePermissionErrorDialogTitle
      : TranscriptionStrings.RecordingMicrophonePermissionErrorDialogTitle;
  }

  private getMicrophonePermissionErrorLabel() {
    return this.isWin32()
      ? TranscriptionStrings.MicrophonePermissionErrorDialogLabel
      : TranscriptionStrings.RecordingMicrophonePermissionErrorDialogLabel;
  }

  public render() {
    const onRecordingStatusChange = async (isPaused: boolean) => {
      try {
        if (isPaused)
          await this.session!.Pause();
        else
          await this.session!.Resume();
      } catch (err) {
        const transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage : TranscriptionStrings.UnknownInitFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnknownInitFailureReason};
        await this.handleFailure(
          transcriptionErrorEvent,
          FailureContinuation.None /*failureContinuation*/,
          'Recording render threw error: ' + ErrorStringFromException(err),
          this.props.history);
      }

      SetRecorderIsPausedInStore(isPaused);
    }

    const onRecordingDone = (): void => {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Transcribe now" }]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      SetRecordingDone();
      SetForegroundTaskInStore(new LiveRecordingForegroundTask(this.session!, this.context, this.props.history));
      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    }

    const enableRecorder = this.state.isRecorderReady && this.state.microphoneState == MicrophoneState.Enabled;
    const showMicPermissionRetryDialog = this.state.shouldShowMicrophonePermissionRetryDialog;

    return (
      <div style={ AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties() }>
        { getHostAppInfoBar() }
        <div style={{ flexGrow: 1 }}>
          <Label styles={ AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, AppLayoutSettings.getAppColorContainer().darkLabelColor) }>{ TranscriptionStrings.CompleteYourRecordingHeading }</Label>
          { enableRecorder ? <Label styles={ AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles() }>{ TranscriptionStrings.RecordingInfoMessage }</Label> : null }
          <div style={ getRecordingContainerStyles() } ref={ this.appVoiceRecorderContainerRef }>
            <AppVoiceRecorder isEnabled={ enableRecorder } type={ 'AppVoiceRecorderProps' } recordingStatusLabel={ this.getRecordingLabel() } stopRecordingButtonLabel={ TranscriptionStrings.TranscribeNowButtonLabel } onRecordingStop={ onRecordingDone } onRecordingStatusChange={ onRecordingStatusChange } />
          </div>
        </div>

        <div style={ AppLayoutSettings.AppCommonStyles.getBottomMessageBarStyles() } ref={ this.warningContainerRef }>
          <div style={ AppLayoutSettings.AppCommonStyles.getWarningMessageContainerStyles() }>
            { this.isAudioBackAvailable || this.state.warningDismissed ? null :
              getBackupDisabledMessageBar()
            }

            { this.state.privacyDisclaimerClosed ? null :
              getPrivacyDisclaimerMessageBar()
            }
          </div>
        </div>
        <TranscriptionAlert/>
        { showMicPermissionRetryDialog ? this.showMicrophonePermissionRetryDialog() : null }
      </div>
    );
  }

  // There are three states we are in when calling getUserMedia to request mic access:
  // 1. Not called
  // 2. Called but pending
  // 3. Approved/Rejected
  // If the browser does not support the permissions API, in #2 we are forced to assume the user hasn't given us permissions, so we show "Please give us permissions".
  // However, it is possible that the user had already given use permissions before and the time we were in #2 was just the time it took for the mic to initialize,
  // which will result in showing "Please give us permissions" briefly.
  // If the browser supports the permissions API, we can preemptively know they have already given us permissions and avoid showing "Please give us permissions" at all
  private async InitializeMicrophoneState() {
    LogEvents.Microphone.Start();
    LogEvents.Microphone.BeginDurationMeasure(DurationMeasureType.Normal, MicrophoneKeys.MicrophoneInitializationDuration);
    // Preemptively check if we have mic permissions if the browser supports permissions API.
    let permissionStatus = await this.getMicPermissionStatus();

    // If we know that we have mic permissions, keep "Setting up the recording..." state until mic is initialized.
    // This prevents us from briefly showing an "Enable microphone permissions to record" message when we know we have permissions.
    const isMicPermissionGranted = permissionStatus != null && permissionStatus.state == PERMISSION_GRANTED;
    if (!isMicPermissionGranted) {
      SetRecorderReadyInStore(true);
    }

    if (permissionStatus != null) {
      LogEvents.Microphone.AddData([{ key: MicrophoneKeys.MicrophonePermissionsPreviouslyGranted, value: isMicPermissionGranted }]);
    }

    await this.startMicrophoneInternal();
  }

  private async startMicrophoneInternal() {
    try {
      await this.session!.StartMicrophone();
      const permissionStatus = await this.getMicPermissionStatus();
      const isMicPermissionGranted = permissionStatus != null && permissionStatus.state == PERMISSION_GRANTED;

      if (this.state.microphoneState == MicrophoneState.Pending || (isMicPermissionGranted && (this.state.microphoneState == MicrophoneState.Disabled)))
        SetMicrophoneStateInStore(MicrophoneState.Enabled);

      LogEvents.Microphone.FlushEventAdhoc([]);
      LogEvents.Microphone.End([{ key: MicrophoneKeys.MicrophoneSuccessfullyInitialized, value: true }]);
    } catch(e) {
      LogEvents.Microphone.AddData([{ key: MicrophoneKeys.MicrophoneSuccessfullyInitialized, value: false }, { key: MicrophoneKeys.MicrophoneInitFailureReason, value: ErrorStringFromException(e) } ]);
      if (isUserMicrophoneError(e)) {
        SetMicrophoneStateInStore(MicrophoneState.Disabled);
        await this.session!.Pause();
        SetMicrophonePermissionRetryDialog(true/* isVisible */);
      } else {
        LogEvents.Microphone.End();
        throw e;
        }
      }
    }

  private getRecordingLabel() {
    if (!this.state.isRecorderReady)
      return TranscriptionStrings.RecordingSettingUpLabel;

    if (this.state.microphoneState == MicrophoneState.Pending)
      return TranscriptionStrings.RecordingEnableMicPermissionsLabel;

    if (this.state.microphoneState == MicrophoneState.Disabled)
      return TranscriptionStrings.RecordingEnableMicPermissionsInBrowserLabel;

    // Recording Pause\Resume label has been removed, in latest UI specs.
    // So return undefined label for all the cases, except required ones like
    // Error scenarios, where label is showing mandatory information to user.
    return undefined;
  }

  private showMicrophonePermissionRetryDialog() {
    LogEvents.Microphone.AddData([{ key: MicrophoneKeys.MicrophonePermissionRetryDialogShown, value: true }]);

    const micPermissionDialogTitle: JSX.Element =
    <Label styles={getTopLabelStyles()}>
      {this.getMicrophonePermissionErrorTitle()}
    </Label>

    return <Dialog
      dialogContentProps={getDialogContentProps(micPermissionDialogTitle)}
      modalProps={getModalProps()}
      hidden={!this.state.shouldShowMicrophonePermissionRetryDialog}
      styles={getDialogStyles()}>
      {!this.isWin32() && <div style={getMicrophoneImageContainerStyle()}>
        <Image alt="" src={require('../../../../assets/' + AppLayoutSettings.AppImageParams.microphonePermissionImageName)} width={AppLayoutSettings.AppImageParams.microphonePermissionWidthPx} height={AppLayoutSettings.AppImageParams.microphonePermissionHeightPx} />
      </div>}
      <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{this.getMicrophonePermissionErrorLabel()}</Label>
      {!this.isWin32() && <Link style={getNeedHelpLinkStyle()} target="_blank" href={MICROPHONE_PERMISSION_HELP_PAGE_URL}>{TranscriptionStrings.RecordingMicrophonePermissionErrorDialogNeedHelp}</Link>}
      <DialogFooter>
        <AppButton
          type={'AppButtonProps'}
          id="MicPermissionNotNowButtonID"
          styles={getSecondaryButtonOverrideStyles()}
          text={TranscriptionStrings.RecordingMicrophonePermissioErrorDialogNotNowButton}
          onExecute={this.onNotNowButtonClick}
        />
        <AppButton
          type={'AppButtonProps'}
          id="MicPermissionTryAgainButtonID"
          text={TranscriptionStrings.RecordingMicrophonePermissioErrorDialogTryAgainButton}
          onExecute={this.onTryAgainConfirmed}
        />
      </DialogFooter>
    </Dialog>
  }

  private onNotNowButtonClick = () => {
    LogEvents.Microphone.End([{key: MicrophoneKeys.MicrophoneSuccessfullyInitialized, value: false}]);
    SetMicrophonePermissionRetryDialog(false/* isVisible */);
    window.location.reload();
  }

  private onTryAgainConfirmed = async () => {
    let permissionStatus = await this.getMicPermissionStatus();
    let isMicPermissionGranted = permissionStatus != null && permissionStatus.state == PERMISSION_GRANTED;
    if (isMicPermissionGranted) {
      SetMicrophonePermissionRetryDialog(false/* isVisible */);
      await this.startMicrophoneInternal();
      SetMicrophoneStateInStore(MicrophoneState.Enabled);
    } else {
      LogEvents.Microphone.IncrementCount(MicrophoneKeys.MicrophonePermissionRetryDailogTryAgainClicked);
      SetMicrophonePermissionRetryDialog(false/* isVisible */);
      await this.startMicrophoneInternal();
    }
  }

  private async handleFailure(transcriptionErrorEvent : ITranscriptionErrorEvent, failureContinuation: FailureContinuation, failureReasonDebug: string, history: any) {
    LogError(LogEventNames.LiveRecording, failureReasonDebug);
    if (this.session) {
      await this.session.Close();
      SetForegroundTaskInStore(null);
      this.session = null;
    }

    LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.FailureReason, value: failureReasonDebug }, { key: LiveRecordingKeys.SuccessfullyEnded, value: false }]);
    LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReasonDebug, isFinalResponse: true });
    SetFailureStateAndRouteToFailure(LogEventNames.LiveRecording, transcriptionErrorEvent, failureContinuation, history);
  }

  private async getMicPermissionStatus() {
    let permissionStatus = null
    const navigatorAny: any = navigator;
    if (navigatorAny.permissions) {
      try {
        permissionStatus = await navigatorAny.permissions.query({ name: 'microphone' });
      } catch (_e) {
        // The browser doesn't support the API, so no-op and fall back to assuming permissions weren't granted
        LogError(LogEventNames.Warning, 'The Browser does not support this API, hence fall back to assuming permissions not granted');
      }
    }
    return permissionStatus;
  }

  private async onErrorCallback(err: Error) {
    const failureReasonDebug: string = 'LiveRecordingSession threw error : ' + err.message;
    let transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage : TranscriptionStrings.UnknownRecordingFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnknownRecordingFailureReason};
    if (!this.isAudioBackAvailable) {
      transcriptionErrorEvent = {errorEventMessage : TranscriptionStrings.UnknownInitFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.UnknownInitFailureReason};
    }

    await this.handleFailure(transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, failureReasonDebug, this.props.history);
  }

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(recordingStore.getState());
    }
  };

  onInternetConnectionStatusChanged(state: InternetState): void {
    if (!this._isMounted) {
      return;
    }
    switch(state) {
      case InternetState.INTERNET_DISCONNECTED: {
        let transcriptionErrorEvent: ITranscriptionErrorEvent = { errorEventMessage: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE, errorEventName: LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE };

        // TranscriptionStrings can be null if the user goes offline before TranscriptionStrings is loaded during boot.
        if (TranscriptionStrings != null) {
            transcriptionErrorEvent = { errorEventMessage: TranscriptionStrings.RecordingLostConnectionFailureReason, errorEventName: TranscriptionTelemetryErrorCodes.RecordingLostConnectionFailureReason };
        }

        // Navigate to the failure page.
        SetFailureStateAndRouteToFailure(
          LogEventNames.Warning,
          transcriptionErrorEvent,
          FailureContinuation.RecoveryonInternetConnected /*failureContinuation*/,
          this.props.history);
      }
      break;
    }
  }
}

function getPrivacyDisclaimerMessageBar() {
  return (
      <AppMessageBar
        containerStyle={getWarningMessageBarStyles()}
        messageBarType={MessageBarType.info}
        labelStyles={getPrivacyDisclaimerTextStyles()}
        styles={getPrivacyDisclaimerStyles()}
        dismissButtonAriaLabel={TranscriptionStrings.CloseLabel}
        onDismiss={()=>{SetPrivacyDisclaimerClosedFlag(true)}}
        headerText={TranscriptionStrings.RecordingPrivacyDisclaimerLabel}
        >
        {TranscriptionStrings.RecordingPrivacyDisclaimerMessage}<Link style={getPrivacyPolicyLinkStyle()} target="_blank" href={PRIVACY_POLICY_URL}>{TranscriptionStrings.RecordingPrivacyPolicyLink}</Link>
      </AppMessageBar>);
}

function getBackupDisabledMessageBar() {
  return (
      <AppMessageBar
        containerStyle={getWarningMessageBarStyles()}
        messageBarType={MessageBarType.warning}
        labelStyles={getBackupDisabledTextStyles()}
        styles= {getBackupDisabledStyles()}
        dismissButtonAriaLabel={TranscriptionStrings.CloseLabel}
        onDismiss={()=>{SetWarningDismissedInStore()}}
        headerText={TranscriptionStrings.RecordingBackupWarningTitleLabel}
        >
        {TranscriptionStrings.RecordingBackupWarningBodyLabel}
      </AppMessageBar>);
}